import { Injectable } from "@angular/core";
import { PropertyIntelligenceChartItem } from "@store/reports/reports.model";
import { ChartConfiguration, Tick } from 'chart.js';
import Charts from 'chart.js/auto';

@Injectable({
    providedIn: 'root'
})
export class PropertyIntelligencePdfService {

    public generateLineChart(propertyIntelligenceChartItems: Array<PropertyIntelligenceChartItem>, labels: Array<string>,
        isMultiAxis: boolean, isPercentChart: boolean = false): string {

        const canvas = document.createElement('canvas');
        canvas.width = 375;

        const data: any = {
            labels: labels,
            datasets: []
        };

        propertyIntelligenceChartItems.forEach(propertyIntelligenceChartItem => {
            data.datasets.push({
                label: propertyIntelligenceChartItem.title,
                data: propertyIntelligenceChartItem.values,
                borderColor: propertyIntelligenceChartItem.color,
                tension: 0.4,
                borderWidth: 1,
                yAxisID: propertyIntelligenceChartItem.verticalAxisId
            })
        });

        const chartSetup: ChartConfiguration<'line', number[], string> = this.getLineChartSetUp(data, isPercentChart);

        if (isMultiAxis) {
            chartSetup.options.scales.y1 = {
                display: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false
                },
                ticks: {
                    font: {
                        size: 6
                    },
                    callback: (value: number, _index: number, _ticks: Tick[]) => {
                        return value >= 1000 ? String(value / 1000).concat('K') : value;
                    }
                }
            }
        }

        const chart = new Charts(canvas.getContext('2d'), chartSetup);

        return chart.toBase64Image();
    }

    public getPdfTitle(chartTitle: string): string {
      return chartTitle.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').replace(/_+/g, '_').toLowerCase();
    }

    private getLineChartSetUp(data: any, isPercentChart: boolean): ChartConfiguration<'line', number[], string> {
        return {
            type: 'line',
            data: data,
            options: {
                responsive: false,
                animation: false,
                plugins: {
                    legend: {
                        display: false
                    }
                },
                scales: {
                    y: {
                        suggestedMin: 0,
                        ticks: {
                            font: {
                                size: 6,
                            },
                            maxTicksLimit: 5,
                            callback: (value: number, _index: number, _ticks: Tick[]) => {
                                if (isPercentChart) {
                                    return `${value}%`;
                                }
                                return value >= 1000 ? String(value / 1000).concat('K') : value;;
                            }
                        }
                    },
                    x: {
                        ticks: {
                            font: {
                                size: 6,
                            }
                        }
                    }
                },
                elements: {
                    point: {
                        pointStyle: false
                    }
                }
            }
        };
    }

    public generateBarChart(chartItems: PropertyIntelligenceChartItem[], labels: string[], isMultiAxis: boolean, isPercentChart: boolean = false): string {
        const canvas = document.createElement('canvas');
        canvas.width = 375;

        const data: any = {
            labels: labels,
            datasets: []
        };

        chartItems.forEach(propertyIntelligenceChartItem => {
            data.datasets.push({
              label: propertyIntelligenceChartItem.title,
              data: propertyIntelligenceChartItem.values,
              backgroundColor: propertyIntelligenceChartItem.color,
              barThickness: 2
            })
        });

        const chartSetup: ChartConfiguration<'bar', number[], string> = this.getBarChartSetUp(data, isPercentChart);

        if (isMultiAxis) {
            chartSetup.options.scales.y1 = {
                display: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false
                },
                ticks: {
                    font: {
                        size: 6
                    },
                    callback: (value: number, _index: number, _ticks: Tick[]) => {
                        return value >= 1000 ? String(value / 1000).concat('K') : value;
                    }
                }
            }
        }

        const chart = new Charts(canvas.getContext('2d'), chartSetup);

        return chart.toBase64Image();
    }

    private getBarChartSetUp(data: any, isPercentChart: boolean): ChartConfiguration<'bar', number[], string> {
        return {
            type: 'bar',
            data: data,
            options: {
                responsive: false,
                animation: false,
                plugins: {
                    legend: {
                        display: false
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            font: {
                                size: 6,
                            },
                            maxTicksLimit: 5,
                            callback: (value: number, _index: number, _ticks: Tick[]) => {
                                if (isPercentChart) {
                                    return `${value}%`;
                                }
                                return value >= 1000 ? String(value / 1000).concat('K') : value;
                            }
                        }
                    },
                    x: {
                        ticks: {
                            font: {
                                size: 6,
                            }
                        }
                    }
                },
                elements: {
                    point: {
                        pointStyle: false
                    }
                }
            }
        };
    }

}
